/**
 * useContainers Hook
 *
 * @module core/modules/waste/hooks
 *
 * @description
 * Hook para gestión de contenedores con CRUD completo.
 */

import { useCallback, useEffect } from 'react';
import { useContainersStore } from '../store';
import { wasteApi, getWasteApiErrorMessage } from '../services';
import type { WasteContainer, CreateContainerDto, UpdateContainerDto } from '../types';
import { FILL_LEVEL_THRESHOLDS } from '../constants';

interface UseContainersOptions {
  projectId: string;
  autoLoad?: boolean;
}

/**
 * Hook para gestión de contenedores
 */
export function useContainers({ projectId, autoLoad = true }: UseContainersOptions) {
  const store = useContainersStore();
  const { containers, isLoading, error, setContainers, addContainer, updateContainer, removeContainer, setLoading, setError } = store;

  /**
   * Cargar contenedores del proyecto
   */
  const loadContainers = useCallback(async () => {
    if (!projectId) return;

    setLoading(true);
    try {
      const data = await wasteApi.containers.getAll({ projectId });
      setContainers(data);
    } catch (err) {
      setError(getWasteApiErrorMessage(err));
    }
  }, [projectId, setLoading, setContainers, setError]);

  /**
   * Crear contenedor
   */
  const createContainer = useCallback(
    async (data: CreateContainerDto): Promise<WasteContainer | null> => {
      try {
        const container = await wasteApi.containers.create(projectId, data);
        addContainer(container);
        return container;
      } catch (err) {
        setError(getWasteApiErrorMessage(err));
        return null;
      }
    },
    [projectId, addContainer, setError]
  );

  /**
   * Actualizar contenedor
   */
  const editContainer = useCallback(
    async (id: string, data: UpdateContainerDto): Promise<WasteContainer | null> => {
      try {
        const container = await wasteApi.containers.update(id, data);
        updateContainer(id, container);
        return container;
      } catch (err) {
        setError(getWasteApiErrorMessage(err));
        return null;
      }
    },
    [updateContainer, setError]
  );

  /**
   * Eliminar contenedor
   */
  const deleteContainer = useCallback(
    async (id: string): Promise<boolean> => {
      try {
        await wasteApi.containers.delete(id);
        removeContainer(id);
        return true;
      } catch (err) {
        setError(getWasteApiErrorMessage(err));
        return false;
      }
    },
    [removeContainer, setError]
  );

  /**
   * Obtener contenedor por ID
   */
  const getContainerById = useCallback(
    (id: string): WasteContainer | undefined => {
      return containers.find((c) => c.id === id);
    },
    [containers]
  );

  /**
   * Obtener contenedores por zona
   */
  const getContainersByZone = useCallback(
    (zoneId: string): WasteContainer[] => {
      return containers.filter((c) => c.zone?.id === zoneId);
    },
    [containers]
  );

  /**
   * Obtener GeoJSON de contenedores
   */
  const loadContainersGeoJSON = useCallback(async () => {
    try {
      return await wasteApi.containers.getGeoJSON(projectId);
    } catch (err) {
      setError(getWasteApiErrorMessage(err));
      return null;
    }
  }, [projectId, setError]);

  // Auto-load al montar
  useEffect(() => {
    if (autoLoad && projectId) {
      loadContainers();
    }
  }, [autoLoad, projectId, loadContainers]);

  // Computed values
  const fullContainers = containers.filter(
    (c) => c.status === 'full' || (c.currentFill && c.currentFill >= FILL_LEVEL_THRESHOLDS.critical)
  );
  const highFillContainers = containers.filter(
    (c) => c.currentFill && c.currentFill >= FILL_LEVEL_THRESHOLDS.high && c.currentFill < FILL_LEVEL_THRESHOLDS.critical
  );
  const activeContainers = containers.filter((c) => c.status === 'active');

  return {
    // State
    containers,
    isLoading,
    error,

    // Actions
    loadContainers,
    createContainer,
    editContainer,
    deleteContainer,
    getContainerById,
    getContainersByZone,
    loadContainersGeoJSON,

    // Computed
    fullContainers,
    highFillContainers,
    activeContainers,
    totalContainers: containers.length,
    needsCollection: fullContainers.length + highFillContainers.length,
  };
}
